import React, {useCallback, useState} from 'react';
import {Button} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import {IconPlus} from '@tabler/icons-react';
import type {WorkflowDefinitionPhaseResponse} from 'src/api/ProactApiClient';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import type {
  IClientKeyed} from 'src/app/workflows/clientIdentity';
import {
  itemKey,
  nextClientKey,
} from 'src/app/workflows/clientIdentity';
import {PhaseCard} from './PhaseCard';
import type {IWorkflowDesignerMetadata} from 'src/app/workflows/types';

export interface PhaseListProps {
  phases: WorkflowDefinitionPhaseResponse[];
  onPhasesChange: (phases: WorkflowDefinitionPhaseResponse[]) => void;
  metadata: IWorkflowDesignerMetadata;
}

export const PhaseList: React.FC<PhaseListProps> = ({
  phases,
  onPhasesChange,
  metadata,
}) => {
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [expandedSet, setExpandedSet] = useState<Set<string | number>>(
    () => new Set<string | number>()
  );

  const toggleExpanded = useCallback((key: string | number) => {
    setExpandedSet(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedSet(new Set(phases.map((phase, i) => itemKey(phase, i))));
  }, [phases]);

  const collapseAll = useCallback(() => {
    setExpandedSet(new Set());
  }, []);

  const handleAddNew = () => {
    const clientKey = nextClientKey();
    onPhasesChange([
      ...phases,
      {
        clientKey,
        name: 'New Phase',
        mutuallyExclusiveCompletion: false,
        actions: [],
      } as WorkflowDefinitionPhaseResponse & IClientKeyed,
    ]);
    setExpandedSet(prev => new Set(prev).add(clientKey));
  };

  const handlePhaseChange = (
    phase: WorkflowDefinitionPhaseResponse,
    index: number
  ) => {
    const newPhases = Array.from(phases);
    newPhases[index] = phase;
    onPhasesChange(newPhases);
  };

  const handleMoveUp = (index: number) => {
    if (index <= 0) return;
    const newPhases = Array.from(phases);
    [newPhases[index - 1], newPhases[index]] = [
      newPhases[index],
      newPhases[index - 1],
    ];
    onPhasesChange(newPhases);
  };

  const handleMoveDown = (index: number) => {
    if (index >= phases.length - 1) return;
    const newPhases = Array.from(phases);
    [newPhases[index], newPhases[index + 1]] = [
      newPhases[index + 1],
      newPhases[index],
    ];
    onPhasesChange(newPhases);
  };

  const handleDeleteRequest = (index: number) => {
    setDeleteIndex(index);
  };

  const handleDeleteConfirm = () => {
    if (deleteIndex !== null) {
      onPhasesChange([
        ...phases.slice(0, deleteIndex),
        ...phases.slice(deleteIndex + 1),
      ]);
      setDeleteIndex(null);
    }
  };

  const deletePhaseLabel =
    deleteIndex !== null ? phases[deleteIndex]?.name ?? 'Unnamed' : '';

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={4}>Phases</Title>
          <Text size="sm" c="dimmed">
            Phases are groups of actions executed in sequential order. Each phase
            must complete before the next one begins.
          </Text>
        </div>
        {phases.length > 1 && (
          <Group gap="xs">
            <Button variant="subtle" size="xs" onClick={expandAll}>
              Expand All
            </Button>
            <Button variant="subtle" size="xs" onClick={collapseAll}>
              Collapse All
            </Button>
          </Group>
        )}
      </Group>

      {phases.length === 0 ? (
        <Alert variant="light" color="gray" title="No phases configured">
          <Text size="sm" mb="sm">
            Phases group actions into sequential steps. Add at least one phase
            to define what the workflow does.
          </Text>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleAddNew}
            size="sm">
            Add First Phase
          </Button>
        </Alert>
      ) : (
        <>
          {phases.map((phase, index) => {
            const key = itemKey(phase, index);
            return (
              <PhaseCard
                key={key}
                phase={phase}
                index={index}
                totalPhases={phases.length}
                allPhases={phases}
                expanded={expandedSet.has(key)}
                onToggleExpanded={() => toggleExpanded(key)}
                onPhaseChange={handlePhaseChange}
                onDeleteRequest={handleDeleteRequest}
                onMoveUp={handleMoveUp}
                onMoveDown={handleMoveDown}
                metadata={metadata}
              />
            );
          })}
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleAddNew}
            size="sm"
            w="fit-content">
            Add Phase
          </Button>
        </>
      )}

      <ConfirmDialog
        opened={deleteIndex !== null}
        onClose={() => setDeleteIndex(null)}
        onConfirm={handleDeleteConfirm}
        title={`Delete Phase ${deleteIndex !== null ? deleteIndex + 1 : ''}: "${deletePhaseLabel}"?`}
        body="Are you sure you want to delete this phase and all its actions? This cannot be undone."
        confirmationButtonColor="red"
        buttonTextYes="Delete"
        buttonTextNo="Cancel"
      />
    </Stack>
  );
};
